import { createBrowserHistory, History } from "history";
import { autorun, computed } from "mobx";
import { observer, Provider } from "mobx-react";
import React, { ReactNode } from "react";
import { IntlProvider } from "react-intl";
import { Redirect, Route, Router, Switch } from "react-router-dom";

import { AsyncApp as AdminApp } from "nvent-web-admin/AsyncApp";
import { RequiredRoleRoute } from "nvent-web-admin/components/RequiredRole";
import { LoadingSection } from "nvent-web/components/LoadingSection";
import { messages } from "nvent-web/i18n";
import Api from "nvent-web/services/Api";
import { Auth0 } from "nvent-web/services/Auth0";
import AppStore from "nvent-web/stores/App";
import { NotificationsStore } from "nvent-web/stores/Notifications";
import { PhotosStore } from "nvent-web/stores/Photos";
import { ProductsStore } from "nvent-web/stores/Products";
import { ProjectsStore } from "nvent-web/stores/Projects";
import { RoomsStore } from "nvent-web/stores/Rooms";
import { TeamInstallersStore } from "nvent-web/stores/TeamInstallers";
import { UserStore } from "nvent-web/stores/User";
import { Role } from "nvent-web/types/Role";

import { AnonymousApp } from "./components/AnonymousApp";
import { AuthenticatedApp } from "./components/AuthenticatedApp";
import { LoginCallback } from "./components/LoginCallback";

interface Injectables {
  auth0: Auth0;
  api: Api;
  history: History;
  app: AppStore;
  user: UserStore;
  projects: ProjectsStore;
  notifications: NotificationsStore;
  rooms: RoomsStore;
  products: ProductsStore;
  photos: PhotosStore;
  teamInstallers: TeamInstallersStore;
}

@observer
export class App extends React.Component {
  private injectables: Injectables;

  @computed
  get isLoading() {
    return this.injectables.auth0.isLoading || this.injectables.user.isLoading;
  }

  constructor(props: {}) {
    super(props);

    const history = createBrowserHistory({
      basename: process.env.REACT_APP_BASENAME,
    });
    const baseUrl = window.location.origin + history.createHref({}).slice(0, -1);

    const notifications = new NotificationsStore();

    const api = new Api(process.env.REACT_APP_API_URL || "", notifications);
    const auth0 = new Auth0(
      baseUrl,
      {
        audience: process.env.REACT_APP_API_AUDIENCE || "",
        domain: process.env.REACT_APP_AUTH0_DOMAIN || "",
        clientID: process.env.REACT_APP_AUTH0_CLIENT_ID || "",
      },
      { notifications }
    );

    const user = new UserStore(auth0, api);
    const rooms = new RoomsStore(user, api, history);

    autorun(() => (api.accessToken = auth0.accessToken));

    this.injectables = {
      auth0,
      api,
      history,
      user,
      app: new AppStore(user, api),
      projects: new ProjectsStore(user, api, history),
      notifications,
      rooms,
      products: new ProductsStore(user, rooms, api, history),
      photos: new PhotosStore(rooms, api),
      teamInstallers: new TeamInstallersStore(api),
    };
  }

  render() {
    const { app, history } = this.injectables;

    return (
      <Provider {...this.injectables}>
        <IntlProvider locale={app.locale} messages={messages[app.locale]}>
          <Router history={history}>{this.renderContent()}</Router>
        </IntlProvider>
      </Provider>
    );
  }

  private renderContent(): ReactNode {
    if (this.isLoading) {
      return <LoadingSection />;
    }

    const { user } = this.injectables;

    return (
      <Switch>
        {/* Auth0 callbacks which need to always work */}
        <Route path="/auth/login/callback" exact={true} component={LoginCallback} />
        <Route path="/auth/logout/callback" exact={true}>
          <Redirect to="/auth/login" />
        </Route>

        {user.isReady ? (
          <Route>
            <Switch>
              <RequiredRoleRoute userRole={Role.admin} path="/admin" exact={false} component={AdminApp} />
              <Route component={AuthenticatedApp} />
            </Switch>
          </Route>
        ) : (
          <Route>
            <AnonymousApp />
          </Route>
        )}
      </Switch>
    );
  }
}

export default App;
